"use client";
import {
  hasExpandedMenuButton
} from "../__chunks/VVC34BID.js";
import {
  MenuContextProvider,
  useMenuProviderContext
} from "../__chunks/QQ7MJHYB.js";
import {
  usePopoverDisclosure
} from "../__chunks/SWFV62AU.js";
import "../__chunks/LW6KKRR6.js";
import "../__chunks/PCYBL4RJ.js";
import "../__chunks/INBEQ5LA.js";
import "../__chunks/5O7V7O3A.js";
import {
  useCompositeTypeahead
} from "../__chunks/KDNXDIJO.js";
import "../__chunks/XVQ3TNPZ.js";
import {
  useHovercardAnchor
} from "../__chunks/FIFZJO4T.js";
import "../__chunks/6QXBPV75.js";
import "../__chunks/X3ATWMYX.js";
import "../__chunks/KSMDNUGB.js";
import {
  Role
} from "../__chunks/EAY52LAK.js";
import "../__chunks/B4BLW6E7.js";
import "../__chunks/7CRP5UGO.js";
import "../__chunks/SNQAYGW2.js";
import "../__chunks/P3UIANB7.js";
import "../__chunks/HJIEYVEO.js";
import "../__chunks/SHA3WOPI.js";
import {
  createComponent,
  createElement,
  createHook
} from "../__chunks/LFJDOMBA.js";
import {
  useStoreState
} from "../__chunks/2ID5JPOO.js";
import {
  useEvent,
  useId,
  useMergeRefs,
  useWrapElement
} from "../__chunks/6WEF4WY5.js";
import "../__chunks/OZVWZODE.js";
import {
  __objRest,
  __spreadProps,
  __spreadValues
} from "../__chunks/2SMRF6AD.js";

// src/menu/menu-button.tsx
import { useEffect, useRef } from "react";
import { getPopupItemRole, getPopupRole } from "@ariakit/core/utils/dom";
import { disabledFromProps, invariant } from "@ariakit/core/utils/misc";
import { sync } from "@ariakit/core/utils/store";
import { jsx } from "react/jsx-runtime";
function getInitialFocus(event, dir) {
  const keyMap = {
    ArrowDown: dir === "bottom" || dir === "top" ? "first" : false,
    ArrowUp: dir === "bottom" || dir === "top" ? "last" : false,
    ArrowRight: dir === "right" ? "first" : false,
    ArrowLeft: dir === "left" ? "first" : false
  };
  return keyMap[event.key];
}
var useMenuButton = createHook(
  (_a) => {
    var _b = _a, { store, focusable, accessibleWhenDisabled, showOnHover } = _b, props = __objRest(_b, ["store", "focusable", "accessibleWhenDisabled", "showOnHover"]);
    const context = useMenuProviderContext();
    store = store || context;
    invariant(
      store,
      process.env.NODE_ENV !== "production" && "MenuButton must receive a `store` prop or be wrapped in a MenuProvider component."
    );
    const ref = useRef(null);
    const parentMenu = store.parent;
    const parentMenuBar = store.menubar;
    const hasParentMenu = !!parentMenu;
    const parentIsMenuBar = !!parentMenuBar && !hasParentMenu;
    const disabled = disabledFromProps(props);
    useEffect(() => {
      return sync(store, ["disclosureElement"], (state) => {
        if (!state.disclosureElement)
          return;
        store == null ? void 0 : store.setState("disclosureElement", ref.current);
      });
    }, [store]);
    const onFocusProp = props.onFocus;
    const onFocus = useEvent((event) => {
      onFocusProp == null ? void 0 : onFocusProp(event);
      if (disabled)
        return;
      if (event.defaultPrevented)
        return;
      store == null ? void 0 : store.setAutoFocusOnShow(false);
      store == null ? void 0 : store.setActiveId(null);
      if (!parentMenuBar)
        return;
      if (!parentIsMenuBar)
        return;
      const { items } = parentMenuBar.getState();
      if (hasExpandedMenuButton(items, event.currentTarget)) {
        store == null ? void 0 : store.show();
      }
    });
    const dir = store.useState(
      (state) => state.placement.split("-")[0]
    );
    const onKeyDownProp = props.onKeyDown;
    const onKeyDown = useEvent((event) => {
      onKeyDownProp == null ? void 0 : onKeyDownProp(event);
      if (disabled)
        return;
      if (event.defaultPrevented)
        return;
      const initialFocus = getInitialFocus(event, dir);
      if (initialFocus) {
        event.preventDefault();
        store == null ? void 0 : store.show();
        store == null ? void 0 : store.setAutoFocusOnShow(true);
        store == null ? void 0 : store.setInitialFocus(initialFocus);
      }
    });
    const onClickProp = props.onClick;
    const onClick = useEvent((event) => {
      onClickProp == null ? void 0 : onClickProp(event);
      if (event.defaultPrevented)
        return;
      if (!store)
        return;
      const isKeyboardClick = !event.detail;
      const { open } = store.getState();
      if (!open || isKeyboardClick) {
        if (!hasParentMenu || isKeyboardClick) {
          store.setAutoFocusOnShow(true);
        }
        store.setInitialFocus(isKeyboardClick ? "first" : "container");
      }
      if (hasParentMenu) {
        store.show();
      }
    });
    props = useWrapElement(
      props,
      (element) => /* @__PURE__ */ jsx(MenuContextProvider, { value: store, children: element }),
      [store]
    );
    if (hasParentMenu) {
      props = __spreadProps(__spreadValues({}, props), {
        render: /* @__PURE__ */ jsx(Role.div, { render: props.render })
      });
    }
    const id = useId(props.id);
    const parentContentElement = useStoreState(parentMenu, "contentElement");
    const role = hasParentMenu || parentIsMenuBar ? getPopupItemRole(parentContentElement, "menuitem") : void 0;
    const contentElement = store.useState("contentElement");
    props = __spreadProps(__spreadValues({
      id,
      role,
      "aria-haspopup": getPopupRole(contentElement, "menu")
    }, props), {
      ref: useMergeRefs(ref, props.ref),
      onFocus,
      onKeyDown,
      onClick
    });
    props = useHovercardAnchor(__spreadProps(__spreadValues({
      store,
      focusable,
      accessibleWhenDisabled
    }, props), {
      showOnHover: (event) => {
        if (typeof showOnHover === "function")
          return showOnHover(event);
        if (showOnHover != null)
          return showOnHover;
        if (hasParentMenu)
          return true;
        if (!parentMenuBar)
          return false;
        const { items } = parentMenuBar.getState();
        return parentIsMenuBar && hasExpandedMenuButton(items);
      }
    }));
    props = usePopoverDisclosure(__spreadValues({
      store,
      toggleOnClick: !hasParentMenu,
      focusable,
      accessibleWhenDisabled
    }, props));
    props = useCompositeTypeahead(__spreadValues({
      store,
      typeahead: parentIsMenuBar
    }, props));
    return props;
  }
);
var MenuButton = createComponent((props) => {
  const htmlProps = useMenuButton(props);
  return createElement("button", htmlProps);
});
if (process.env.NODE_ENV !== "production") {
  MenuButton.displayName = "MenuButton";
}
export {
  MenuButton,
  useMenuButton
};
